/**
 * Phase 5 — Choose
 *
 * "I choose..."
 * Before the choice, a "Message from Within" Insight Card is offered.
 * Claude generates 3 grounded suggestions based on the pattern + shift.
 * User picks one (editable), or writes their own.
 * The choice is stored verbatim and displayed as a shareable artifact card.
 */
import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useScreenId } from '../../lib/useScreenId'
import { BACK_DEPTH, useScreenTrail, useSessionBack } from '../../lib/sessionBack'
import Retained from '../ui/Retained'
import type { ScreenKey } from '../../lib/screenIds'
import { useTranslation } from 'react-i18next'
import { localizeEmotionLabel } from '../../data/emotions.pl'
import { localizeArchetype } from '../../lib/archetypes'
import { db } from '../../lib/supabase'
import { callClaude } from '../../lib/claude'
import { parseOptions } from '../../lib/parseOptions'
import { pickVariation } from '../../data/promptVariations'
import { saveChoiceCard } from '../../lib/shareCard'
import { useVoiceInput } from '../../hooks/useVoiceInput'
import InsightCard from './InsightCard'
import { SYMBOLS } from '../../data/symbols'
import type { SessionMessage, SessionMode } from '../../types/session'
import IntuitiveChoice from '../ui/IntuitiveChoice'
import type { ChoiceState } from '../ui/IntuitiveChoice'

interface ChooseProps {
  sessionId: string
  userId?: string
  emotion: string
  firstName?: string
  patternReflection?: string
  reframeResponse?: string
  storyText?: string
  personalSymbol?: string | null
  lifePathNumber?: number
  seriesContext?: string
  /**
   * Mind-level lens — currently informational; Sedona Method release step
   * is always available regardless of mode. Accepted to keep prop parity
   * with SessionPage and for potential future branching.
   */
  sessionMode?: SessionMode
  onComplete: (choiceText: string) => void
}

type UIState =
  | 'loading-suggestions'
  | 'card-offer'        // Message from Within offered before input
  | 'showing-card'      // InsightCard overlay
  | 'new-possibility'   // intuitive choice — new perspective before writing choice
  | 'sedona'            // Sedona Method release — 3-question contemplation (always available)
  | 'input'
  | 'witnessing'
  | 'artifact'
  | 'identity-shift'    // "Who are you when you choose this?" — final deepening step
  | 'error'

/** Which numbered screen each state shows (see lib/screenIds.ts). */
const SCREEN_OF: Record<UIState, ScreenKey | null> = {
  'loading-suggestions': 'choose.cardOffer',
  'card-offer':          'choose.cardOffer',
  // The insight card is on screen in Choose's place and reports its own number.
  'showing-card':        null,
  'new-possibility':     'choose.possibility',
  'sedona':              'choose.sedona',
  'input':               'choose.choice',
  'witnessing':          'choose.choice',
  'artifact':            'choose.artifact',
  'identity-shift':      'choose.identity',
  'error':               'choose.error',
}

function parseSuggestions(text: string): string[] {
  return text
    .split('\n')
    .map(l => l.trim())
    .filter(l => /^[1-3][.)]\s/.test(l))
    .map(l => l.replace(/^[1-3][.)]\s*/, '').trim())
    .filter(l => l.length > 4)
    .slice(0, 3)
}

function DownloadIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" style={{ flexShrink: 0 }}>
      <path d="M7.5 2v8M4.5 7.5l3 3 3-3M2.5 12h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

// ─── Sedona Method — three release questions ────────────────────────────
// Static prompts (no Claude calls needed). Lester Levenson's release method:
// "Could you let it go?" → "Would you?" → "When?"
// Each is a contemplative gateway, not a logic test — answers are felt, not analyzed.
// The three questions now live in the locale (choose.sedona1/2/3) and are
// assembled inside the component, keyed 1/2/3 to match sedonaStep.

export default function Choose({
  sessionId, userId = '', emotion, firstName = '',
  patternReflection = '', reframeResponse = '',
  storyText = '', personalSymbol = null, lifePathNumber, seriesContext,
  sessionMode: _sessionMode,  // accepted for prop parity; not used yet — Sedona is always-on
  onComplete,
}: ChooseProps) {
  const { t, i18n } = useTranslation()

  /*
   * The three Sedona gateway questions. Read from the locale rather than the
   * module-level record, which cannot be translated where it sits. Keyed 1/2/3
   * to match sedonaStep exactly, so the sequence order is preserved.
   */
  const sedonaQuestions: Record<1 | 2 | 3, string> = {
    1: t('choose.sedona1'),
    2: t('choose.sedona2'),
    3: t('choose.sedona3'),
  }

  // Suppress unused-var warning while remaining explicit about prop parity.
  void _sessionMode

  const [uiState, setUiState] = useState<UIState>('loading-suggestions')
  useScreenId(SCREEN_OF[uiState])
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [possibilityOptions, setPossibilityOptions] = useState<string[]>([])
  const [optionsLoading, setOptionsLoading] = useState(true)
  const [suggestionRaw, setSuggestionRaw] = useState<string>('')
  const [choiceText, setChoiceText] = useState('')
  const [isStuck, setIsStuck] = useState(false)
  const [witnessLine, setWitnessLine] = useState<string | null>(null)
  const [identityText, setIdentityText] = useState('')

  // ─── Sedona Method state (always-on optional release step) ────────────
  // sedonaStep — current question (1, 2, or 3)
  // sedonaAnswers — accumulated answers across all 3 questions
  // sedonaInput — textarea input for question 3 ("When?") — q1 and q2 are buttons
  const [sedonaStep, setSedonaStep] = useState<1 | 2 | 3>(1)
  const [sedonaAnswers, setSedonaAnswers] = useState<string[]>([])
  const [sedonaInput, setSedonaInput] = useState('')
  const voice = useVoiceInput()

  // The Message from Within card, once opened, stays alive (hidden) when the
  // person moves on, so Back from the screens after it lands on the card's last
  // screen as it was left instead of on a rebuilt one.
  // What the person chose on 5.2. Kept here, not in the picker, so it
  // outlives the picker: Back to this screen shows the same chips and words, and
  // Continue with nothing touched hands on the same answer (#68).
  const [possibilityPick, setPossibilityPick] = useState<ChoiceState | undefined>()

  const [cardOpened, setCardOpened] = useState(false)

  // The choice the current witness line was written for. Going back to the
  // choice and continuing with the same words shows that line again.
  const witnessFor = useRef<string | null>(null)

  // ─── Back ──────────────────────────────────────────────────────────────────
  // The screens Choose has shown, in order (lib/sessionBack.ts). 'showing-card'
  // is one of them: the hosted card has its own screens and is asked first.
  useScreenTrail<UIState>({
    screen: uiState,
    go: setUiState,
    kind: s =>
      s === 'loading-suggestions' || s === 'witnessing' ? 'loading'
        : s === 'error' ? 'error'
        : 'rest',
  })
  // Sedona's three questions are one screen as far as the trail is concerned.
  // Back steps through them itself, and only from the first does it hand over.
  useSessionBack(() => {
    if (uiState !== 'sedona' || sedonaStep === 1) return 'first'
    setSedonaStep((sedonaStep - 1) as 1 | 2)
    return 'handled'
  }, BACK_DEPTH.step)

  // ─── Phase 2: Archetype hint (Awakening tier only) ────────────────────
  // Surfaces the user's highest-confidence active archetype (>=0.85) as a
  // subtle integration cue while they're writing their choice. Loads in the
  // background — never blocks the UI.
  const [activeArchetype, setActiveArchetype] = useState<{
    display_name: string
    integration_aspect: string
  } | null>(null)

  // The rotating sentence stem ("Today I choose...") comes from the catalogue in
  // the session's language. It was an English-only list in code, so a Polish
  // session showed "I am ready to..." (Andy's iPhone, 2026-10-14).
  const choiceStem = pickVariation(t('variations.choiceStem', { returnObjects: true }), sessionId)

  // Shown when the model's possibilities cannot be used (the call failed or
  // returned too few). Read from the catalogue so the fallback is in the
  // session's language too.
  const possibilityFallback = t('choose.possibilityOptions', { returnObjects: true }) as string[]

  useEffect(() => {
    // Load AI-driven possibility options concurrently with suggestions
    callClaude({
      phase: 20, emotion, intensity: 5,
      messages: [{ role: 'user', content: '[Generate options]' }],
      firstName, sessionId,
      optionContext: `Question: "What small new choice or perspective feels possible now?" Pattern named: "${patternReflection}". Reframe explored: "${reframeResponse}". Generate 9 short possibilities — small choices or perspective shifts this specific person could make, grounded entirely in the pattern they named and the reframe they just explored. VARY the structure: some start with "I can...", some start with a verb ("Letting go of...", "Staying with..."), some are perspective shifts rather than actions. Each option must be specific enough that it could only appear in THIS session — if it sounds like a generic wellness affirmation, rewrite it. No two options should feel like variations of the same idea.`,
    }).then(result => {
      if (!('error' in result)) {
        const parsed = parseOptions(result.response)
        if (parsed.length >= 7) setPossibilityOptions(parsed)
        else setPossibilityOptions(possibilityFallback)
      } else {
        setPossibilityOptions(possibilityFallback)
      }
      setOptionsLoading(false)
    })

    const msgs: SessionMessage[] = [
      {
        role: 'user',
        content: `Pattern named: "${patternReflection}". Shift explored: "${reframeResponse}". [Generate 3 choice suggestions]`,
      },
    ]
    callClaude({ phase: 5, emotion, intensity: 5, messages: msgs, firstName, sessionId, lifePathNumber, seriesContext })
      .then(result => {
        if (!('error' in result)) {
          setSuggestionRaw(result.response)
          const parsed = parseSuggestions(result.response)
          if (parsed.length >= 2) setSuggestions(parsed)
        }
        // After suggestions load → card offer first
        setUiState('card-offer')
      })

    // ─── Phase 2: Load active archetype hint (Awakening only) ──────────────
    // Fire-and-forget — never blocks the UI. Only surfaces the highest-
    // confidence (>= 0.85) active, user-confirmed archetype.
    if (userId) {
      db()
        .from('profiles')
        .select('subscription_tier')
        .eq('id', userId)
        .maybeSingle()
        .then(async ({ data }) => {
          const subscriptionTier = (data as { subscription_tier: string } | null)?.subscription_tier ?? 'free'
          if (subscriptionTier !== 'premium') return

          // Pull the user's strongest confirmed active archetype
          const { data: assignments } = await db()
            .from('user_archetype_assignments')
            .select('archetype_id, intensity')
            .eq('user_id', userId)
            .eq('user_confirmed', true)
            .eq('state', 'active')
            .order('intensity', { ascending: false })
            .limit(1)
            .maybeSingle()

          if (!assignments) return
          const a = assignments as { archetype_id: string; intensity: number }
          // 0.85 confidence floor — only surface when we're sure
          if (a.intensity < 0.85) return

          const { data: def } = await db()
            .from('archetype_definitions')
            .select('display_name, integration_aspect')
            .eq('id', a.archetype_id)
            .maybeSingle()

          if (def) {
            setActiveArchetype(localizeArchetype(a.archetype_id, def as { display_name: string; integration_aspect: string }, t))
          }
        })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function handleConfirm() {
    if (!choiceText.trim()) return
    const choice = choiceText.trim()
    // Same words as the witness line already on hand: show it again.
    if (witnessFor.current === choice) {
      setUiState('artifact')
      return
    }
    // New words: the old line was about the old choice.
    setWitnessLine(null)
    setUiState('witnessing')

    const turn0UserMsg = `Pattern named: "${patternReflection}". Shift explored: "${reframeResponse}". [Generate 3 choice suggestions]`
    const turn0AssistantMsg = suggestionRaw || suggestions.map((s, i) => `${i + 1}. ${s}`).join('\n')

    const result = await callClaude({
      phase: 5,
      emotion,
      intensity: 5,
      messages: [
        { role: 'user',      content: turn0UserMsg },
        { role: 'assistant', content: turn0AssistantMsg },
        { role: 'user',      content: `${choiceStem} ${choiceText.trim()}` },
      ],
      firstName,
      sessionId,
      lifePathNumber,
      seriesContext,
    })

    if (!('error' in result)) setWitnessLine(result.response)
    witnessFor.current = choice
    setUiState('artifact')
  }

  const today = new Date().toLocaleDateString(i18n.language, { month: 'long', day: 'numeric', year: 'numeric' })

  // The witness line is meant to be the user's own FIRST-PERSON declaration
  // ("I came in carrying [emotion]. I am leaving choosing [choice].") — never a
  // name, "You came", or "They came" (see the facilitate phase-5 prompt). The
  // model occasionally ignores this and emits the user's name, an empty quoted
  // name slot (e.g. `'' came in carrying...` when no name is set), or second/
  // third person. The Edge Function is the primary source of truth; this is a
  // belt-and-suspenders guard that always normalizes to first person.
  const displayWitnessLine = (() => {
    if (!witnessLine) return witnessLine
    let line = witnessLine.trim()
    // 1. Strip the user's name if the model used it.
    if (firstName) {
      const escaped = firstName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      line = line.replace(new RegExp(`\\b${escaped}[,.]?\\s+`, 'gi'), '')
    }
    // 2. Replace an empty / quoted name slot at the start with "I".
    line = line.replace(/^["'“”]\s*["'“”][,.]?\s*/, 'I ')
    // 3. Force first person on the witness verbs, whatever the model emitted.
    line = line
      .replace(/\b(?:you|they)\s+came\b/gi, 'I came')
      .replace(/\b(?:you|they)\s+are\b/gi, 'I am')
      .replace(/\b(?:you|they)['’]re\b/gi, "I'm")
    // 4. If the name was the subject and got stripped, the line now starts
    //    "came …" — restore the subject.
    line = line.replace(/^\s*came\b/i, 'I came')
    return line.trim()
  })()

  /** The screen for the current state. The insight card is drawn separately, below. */
  function renderScreen(): ReactNode {
    // ── Loading suggestions ───────────────────────────────────────────────────
    if (uiState === 'loading-suggestions') {
      return (
        <div className="flex-1 flex flex-col page-enter">
          <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full">
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="flex gap-1 mb-3">
                {[0, 1, 2].map(i => (
                  <div key={i} className="w-1.5 h-1.5 rounded-full"
                    style={{ background: '#9B72C0', animation: `pulse 1.4s ease-in-out ${i * 0.2}s infinite` }} />
                ))}
              </div>
              <p className="text-xs" style={{ color: '#6B5F7A' }}>{t('choose.listeningToWhat')}</p>
            </div>
          </div>
        </div>
      )
    }

    // ── Message from Within card offer ────────────────────────────────────────
    if (uiState === 'card-offer') {
      return (
        <div className="flex-1 flex flex-col page-enter">
          <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full">

            <div className="flex-1 flex flex-col items-center justify-center message-appear text-center">

              <div
                className="w-12 h-12 rounded-full flex items-center justify-center mb-8"
                style={{ background: 'radial-gradient(circle, rgba(80,16,48,0.4) 0%, rgba(80,16,48,0.1) 70%)', border: '1px solid rgba(220,120,150,0.2)' }}
              >
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: 'rgba(220,120,150,0.7)' }} />
              </div>

              <p className="font-display text-2xl leading-snug mb-4 max-w-xs"
                style={{ color: '#1C1128', fontWeight: 400, fontStyle: 'italic' }}>
                {t('choose.beforeYouChoose')}
              </p>

              <p className="text-sm leading-relaxed mb-10 max-w-xs" style={{ color: '#6B5F7A' }}>
                {t('choose.messageWaiting')}
                {t('choose.somethingWantsToSpeak')}
              </p>

              <button
                onClick={() => { setCardOpened(true); setUiState('showing-card') }}
                className="w-full max-w-xs py-4 rounded-xl font-medium tracking-wide mb-3"
                style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
              >
                {t('choose.receiveMessage')}
              </button>
              <button
                onClick={() => setUiState('new-possibility')}
                className="w-full max-w-xs py-3 rounded-xl text-sm"
                style={{ background: 'transparent', border: 'none', color: '#C4B5D4', cursor: 'pointer' }}
              >
                {t('choose.readyToChoose')}
              </button>
            </div>
          </div>
        </div>
      )
    }

    // ── Message from Within InsightCard ──────────────────────────────────────
    // Drawn by the slot at the end of this component, not here, so it can stay
    // alive after the person moves on from it.
    if (uiState === 'showing-card') return null

    // ── New Possibility intuitive choice ─────────────────────────────────────
    if (uiState === 'new-possibility') {
      return (
        <div className="flex-1 flex flex-col page-enter">
          <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full">
            <IntuitiveChoice
              initialState={possibilityPick}
              onStateChange={setPossibilityPick}
              prompt={t('choose.choiceQuestion')}
              options={possibilityOptions}
              isLoadingOptions={optionsLoading}
              placeholder={t('choose.orWriteOwn')}
              ctaLabel={t('session.continue')}
              skipLabel={t('choose.skipContinue')}
              // Route to Sedona release step — always-on contemplative pause before
              // committing to the choice. User can skip from inside Sedona.
              onComplete={() => setUiState('sedona')}
            />
          </div>
        </div>
      )
    }

    // ── Sedona Method release step (always available) ──────────────────────
    // Three contemplative questions — no Claude call needed. Q1 and Q2 use
    // pill buttons (Yes / Not yet / Maybe); Q3 ("When?") uses a textarea.
    // Answers are not persisted to the data model — this is a felt-sense pause.
    if (uiState === 'sedona') {
      const currentQuestion = sedonaQuestions[sedonaStep]

      // Advance helper: stores the answer, advances or finishes
      const advance = (answer: string) => {
        // Placed by position, so answering a question again after going back
        // replaces the earlier answer instead of adding a fourth.
        const updated = [...sedonaAnswers.slice(0, sedonaStep - 1), answer]
        setSedonaAnswers(updated)
        if (sedonaStep === 3) {
          // Final question answered → on to the choice input
          setUiState('input')
        } else {
          setSedonaStep((sedonaStep + 1) as 1 | 2 | 3)
        }
      }

      return (
        <div className="flex-1 flex flex-col page-enter">
          <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full">

            {/* Supertitle — Choose rose accent */}
            <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#C45272' }}>
              {t('choose.sedonaRelease')}
            </p>

            {/* Step indicator */}
            <p className="text-xs mb-6" style={{ color: '#9B8FAA' }}>
              {t('choose.questionOfThree', { n: sedonaStep })}
            </p>

            {/* What "this" refers to — names what's being released so the Sedona
                questions ("Could you let this go?") have a clear referent. Shown on
                all three steps to keep "this" anchored throughout the release. */}
            <div className="mb-6 px-4 py-3 rounded-xl" style={{
              background: 'rgba(196,82,114,0.04)',
              borderTop: '1.5px solid #E4D5EE',
              borderRight: '1.5px solid #E4D5EE',
              borderBottom: '1.5px solid #E4D5EE',
              borderLeft: '3px solid #C45272',
            }}>
              <p className="text-xs mb-1" style={{ color: '#6B5F7A' }}>{t('choose.lettingGoOf')}</p>
              <p className="text-sm leading-relaxed" style={{ color: '#3D1F62' }}>
                {patternReflection
                  ? patternReflection
                  : t('session.emotionYouCameInWith', { emotion: localizeEmotionLabel(emotion, i18n.language) })}
              </p>
            </div>

            {/* Question card — uses Choose rose accent border-top recipe */}
            <div className="mb-8 px-5 py-6 rounded-2xl message-appear" style={{
              background: 'rgba(196,82,114,0.04)',
              borderTop: '3px solid #C45272',
              borderRight: '1.5px solid #E4D5EE',
              borderBottom: '1.5px solid #E4D5EE',
              borderLeft: '1.5px solid #E4D5EE',
            }}>
              <p className="font-display italic text-xl leading-snug" style={{ color: '#1C1128' }}>
                {currentQuestion}
              </p>
            </div>

            {/* Q1 and Q2 use pill response buttons; Q3 uses textarea */}
            {sedonaStep < 3 ? (
              <div className="flex flex-col gap-3">
                {[t('arcs.yes'), t('arc.notYet'), t('choose.maybe')].map(label => (
                  <button
                    key={label}
                    onClick={() => advance(label)}
                    className="w-full py-3 rounded-full text-sm font-medium transition-all"
                    style={{
                      background: 'white',
                      border: '1.5px solid #C45272',
                      color: '#C45272',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#C45272'; e.currentTarget.style.color = 'white' }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = '#C45272' }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : (
              <div className="rounded-2xl px-5 py-5" style={{
                background: 'rgba(196,82,114,0.04)',
                borderTop: '3px solid #C45272',
                borderRight: '1.5px solid #E4D5EE',
                borderBottom: '1.5px solid #E4D5EE',
                borderLeft: '1.5px solid #E4D5EE',
              }}>
                <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#C45272' }}>
                  {t('choose.yourAnswer')}
                </p>
                <textarea
                  value={sedonaInput}
                  onChange={e => setSedonaInput(e.target.value)}
                  placeholder={t('choose.whenPlaceholder')}
                  rows={3}
                  maxLength={500}
                  autoFocus
                  className="w-full px-4 py-3 rounded-xl border text-sm leading-relaxed resize-none focus:outline-none"
                  style={{ borderColor: '#C45272', background: 'white', color: '#1C1128' }}
                  onFocus={e => { e.target.style.borderColor = '#C45272'; e.target.style.boxShadow = '0 0 0 3px rgba(196,82,114,0.18)' }}
                  onBlur={e => { e.target.style.borderColor = '#C45272'; e.target.style.boxShadow = 'none' }}
                />
              </div>
            )}

            <div className="flex-1" />

            {/* Continue button only on Q3 — Q1 and Q2 advance via the response buttons */}
            {sedonaStep === 3 && (
              <button
                onClick={() => advance(sedonaInput.trim() || 'unanswered')}
                disabled={!sedonaInput.trim()}
                className="w-full py-4 rounded-xl font-medium tracking-wide mt-6"
                style={{
                  background: sedonaInput.trim() ? '#3D1F62' : '#E4D5EE',
                  color: sedonaInput.trim() ? 'white' : '#9B8FAA',
                  border: 'none',
                  cursor: sedonaInput.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                {t('session.continue')}
              </button>
            )}

            {/* Skip link — bypasses release entirely */}
            <button
              onClick={() => setUiState('input')}
              className="w-full py-2 text-sm mt-3"
              style={{ background: 'none', border: 'none', color: '#9B8FAA', cursor: 'pointer', textDecoration: 'underline' }}
            >
              {t('session.skip')}
            </button>
          </div>
        </div>
      )
    }

    return (
      <div className="flex-1 flex flex-col page-enter">
        <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full">

          <div className="mb-8">
            <p className="text-xs font-medium uppercase tracking-widest" style={{ color: '#9B72C0' }}>{t('session.phases.choose')}</p>
          </div>

          {/* Input state */}
          {(uiState === 'input' || uiState === 'witnessing') && (
            <div className="flex-1 flex flex-col message-appear">

              <p className="font-display text-xl leading-snug mb-2" style={{ color: '#1C1128', fontStyle: 'italic' }}>
                {t('choose.fromHereFinish')}
              </p>
              <p className="font-display text-2xl mb-6" style={{ color: '#3D1F62', fontWeight: 400 }}>
                {choiceStem}
              </p>

              {suggestions.length > 0 && !choiceText && (
                <div className="mb-5 message-appear">
                  <p className="text-xs mb-3" style={{ color: '#9B8FAA' }}>
                    {t('choose.basedOnSession')}
                  </p>
                  <div className="flex flex-col gap-2">
                    {suggestions.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          setChoiceText(s)
                          setIsStuck(false)
                        }}
                        className="text-left px-4 py-3 rounded-xl text-sm leading-relaxed transition-all"
                        style={{
                          // Rose-tinted background — Choose phase accent at ~4% opacity
                          background: 'rgba(196,82,114,0.04)',
                          // Left-border accent recipe for small inline context cards
                          borderTop: '1.5px solid #E4D5EE',
                          borderRight: '1.5px solid #E4D5EE',
                          borderBottom: '1.5px solid #E4D5EE',
                          borderLeft: '3px solid #C45272',
                          color: '#3D1F62',
                          cursor: 'pointer',
                        }}
                        onMouseEnter={e => {
                          // Hover: brighten the three neutral sides while preserving the accent
                          const el = e.currentTarget as HTMLButtonElement
                          el.style.borderTopColor = '#9B72C0'
                          el.style.borderRightColor = '#9B72C0'
                          el.style.borderBottomColor = '#9B72C0'
                        }}
                        onMouseLeave={e => {
                          const el = e.currentTarget as HTMLButtonElement
                          el.style.borderTopColor = '#E4D5EE'
                          el.style.borderRightColor = '#E4D5EE'
                          el.style.borderBottomColor = '#E4D5EE'
                        }}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Choice textarea — wrapped in Choose rose card (#C45272) at 4% tint */}
              <div className="rounded-2xl px-5 py-5" style={{
                background: 'rgba(196,82,114,0.04)',
                borderTop: '3px solid #C45272',
                borderRight: '1.5px solid #E4D5EE',
                borderBottom: '1.5px solid #E4D5EE',
                borderLeft: '1.5px solid #E4D5EE',
              }}>
                <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#C45272' }}>
                  {t('choose.yourChoice')}
                </p>
                <textarea
                  value={choiceText}
                  onChange={e => { setChoiceText(e.target.value); setIsStuck(false) }}
                  placeholder={suggestions.length > 0 ? t('choose.orWriteOwn') : t('choose.hintPlaceholder')}
                  rows={3}
                  maxLength={500}
                  autoFocus={suggestions.length === 0}
                  className="w-full px-4 py-4 rounded-xl border text-sm leading-relaxed resize-none focus:outline-none"
                  // Border is always Choose phase accent rose
                  style={{ borderColor: '#C45272', background: 'white', color: '#1C1128' }}
                  onFocus={e => { e.target.style.borderColor = '#C45272'; e.target.style.boxShadow = '0 0 0 3px rgba(196,82,114,0.18)' }}
                  onBlur={e => { e.target.style.borderColor = '#C45272'; e.target.style.boxShadow = 'none' }}
                />
                {voice.supported && (
                  <button
                    type="button"
                    onClick={() => voice.isListening
                      ? voice.stop()
                      : voice.start(t => setChoiceText(prev => prev ? `${prev} ${t}` : t))
                    }
                    className="flex items-center gap-1 text-xs mt-2 self-end"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: voice.isListening ? '#C45272' : '#9B8FAA' }}
                  >
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                      <rect x="4" y="0.5" width="5" height="7" rx="2.5" stroke="currentColor" strokeWidth="1.2" />
                      <path d="M1.5 6.5a5 5 0 0 0 10 0" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                      <line x1="6.5" y1="11.5" x2="6.5" y2="12.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                    </svg>
                    {voice.isListening ? t('session.voice.listening') : t('session.voice.dictate')}
                  </button>
                )}
              </div>

              {!choiceText && suggestions.length === 0 && (
                <button
                  onClick={() => setIsStuck(!isStuck)}
                  className="text-xs mt-3 self-start transition-all"
                  style={{ color: '#9B72C0', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  {isStuck ? t('choose.hideHint') : t('choose.notSureWhatToWrite')}
                </button>
              )}

              {isStuck && !choiceText && (
                <div
                  className="mt-3 px-4 py-3 rounded-xl message-appear"
                  style={{
                    // Rose-tinted background — Choose phase accent at ~4% opacity
                    background: 'rgba(196,82,114,0.04)',
                    // Left-border accent recipe for small inline context cards
                    borderTop: '1.5px solid #E4D5EE',
                    borderRight: '1.5px solid #E4D5EE',
                    borderBottom: '1.5px solid #E4D5EE',
                    borderLeft: '3px solid #C45272',
                  }}
                >
                  <p className="text-sm leading-relaxed" style={{ color: '#6B5F7A' }}>
                    {t('choose.evenSmallCounts')}
                    {t('choose.hintExample')}
                  </p>
                </div>
              )}

              {/* Archetype hint — Phase 2, Awakening only.                   */}
              {/* Subtle integration cue surfaced when the user has a high-   */}
              {/* confidence active archetype. Quiet by design — just present.*/}
              {activeArchetype && (
                <div
                  className="mt-4 px-4 py-3 rounded-xl message-appear"
                  style={{
                    background: 'rgba(123,64,168,0.04)',
                    borderLeft: '3px solid #7B40A8',
                    borderTop: '1px solid #E4D5EE',
                    borderRight: '1px solid #E4D5EE',
                    borderBottom: '1px solid #E4D5EE',
                  }}
                >
                  <p className="text-[10px] font-medium uppercase tracking-widest mb-1.5" style={{ color: '#7B40A8' }}>
                    {activeArchetype.display_name}
                  </p>
                  <p className="text-xs leading-relaxed" style={{ color: '#6B5F7A', lineHeight: 1.65 }}>
                    {activeArchetype.integration_aspect}
                  </p>
                </div>
              )}

              <div className="flex-1" />

              <div className="mt-6" />
              <button
                onClick={handleConfirm}
                disabled={!choiceText.trim() || uiState === 'witnessing'}
                className="w-full py-4 rounded-xl font-medium tracking-wide"
                style={{
                  background: choiceText.trim() ? '#3D1F62' : '#E4D5EE',
                  color: choiceText.trim() ? 'white' : '#9B8FAA',
                  border: 'none',
                  cursor: choiceText.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                {uiState === 'witnessing' ? t('choose.oneMoment') : t('choose.thisIsMyChoice')}
              </button>
            </div>
          )}

          {/* Artifact card */}
          {uiState === 'artifact' && (
            <div className="flex-1 flex flex-col items-center justify-center message-appear">

              {(() => {
                const sym = personalSymbol ? SYMBOLS.find(s => s.id === personalSymbol) : null
                return (
                  <div
                    className="w-full max-w-sm px-8 py-10 rounded-2xl text-center mb-8"
                    style={{
                      background: 'linear-gradient(145deg, #3D1F62 0%, #6B3FA0 100%)',
                      boxShadow: '0 8px 40px rgba(61,31,98,0.25)',
                    }}
                  >
                    {sym && (
                      <div className="flex justify-center mb-5">
                        <svg width="32" height="32" viewBox="0 0 100 100" fill="none">
                          <path d={sym.path} stroke="rgba(255,255,255,0.45)" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                        </svg>
                      </div>
                    )}
                    <p className="text-xs font-medium uppercase tracking-widest mb-6"
                      style={{ color: 'rgba(255,255,255,0.5)' }}>
                      {choiceStem.replace('...', '')}
                    </p>
                    <p className="font-display text-xl leading-relaxed mb-8"
                      style={{ color: 'white', fontStyle: 'italic', fontWeight: 400 }}>
                      {choiceText}
                    </p>
                    <div style={{ width: '40px', height: '1px', background: 'rgba(255,255,255,0.2)', margin: '0 auto 1.5rem' }} />
                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                      {today} · {localizeEmotionLabel(emotion, i18n.language)}
                    </p>
                  </div>
                )
              })()}

              {displayWitnessLine && (
                <p className="text-sm text-center mb-4 font-display italic" style={{ color: '#6B5F7A', lineHeight: 1.6 }}>
                  {displayWitnessLine}
                </p>
              )}
              <p className="text-sm text-center mb-6" style={{ color: '#6B5F7A', lineHeight: 1.6 }}>
                {t('choose.thatIsYours')}
              </p>

              <button
                onClick={() => saveChoiceCard({ choiceText, choiceStem, emotion, date: today })}
                className="w-full max-w-sm py-3 rounded-xl text-sm flex items-center justify-center gap-2 mb-3"
                style={{ background: 'transparent', border: '1.5px solid #E4D5EE', color: '#6B5F7A', cursor: 'pointer' }}
              >
                <DownloadIcon />
                {t('choose.saveAsImage')}
              </button>

              <button
                onClick={() => setUiState('identity-shift')}
                className="w-full max-w-sm py-4 rounded-xl font-medium tracking-wide"
                style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
              >
                {t('session.continue')}
              </button>
            </div>
          )}

          {/* Identity Shift — final deepening step */}
          {uiState === 'identity-shift' && (
            <div className="flex-1 flex flex-col message-appear">

              {/* The choice "this" refers to — shown so the question has a clear
                  referent instead of relying on recall from the previous screen. */}
              {choiceText && (
                <div className="mb-6 px-4 py-3 rounded-xl" style={{
                  background: 'rgba(196,82,114,0.04)',
                  borderTop: '1.5px solid #E4D5EE',
                  borderRight: '1.5px solid #E4D5EE',
                  borderBottom: '1.5px solid #E4D5EE',
                  borderLeft: '3px solid #C45272',
                }}>
                  <p className="text-xs mb-1" style={{ color: '#6B5F7A' }}>{t('choose.whatYouChose')}</p>
                  <p className="text-sm leading-relaxed font-display italic" style={{ color: '#3D1F62' }}>
                    {choiceStem.replace('...', '')} {choiceText}
                  </p>
                </div>
              )}

              <div className="mb-8">
                <p className="text-base leading-relaxed font-display italic mb-2" style={{ color: '#1C1128' }}>
                  {t('choose.whoAreYou')}
                </p>
                <p className="text-sm leading-relaxed" style={{ color: '#6B5F7A' }}>
                  {t('choose.notWhoYouve')}
                </p>
              </div>

              <textarea
                value={identityText}
                onChange={e => setIdentityText(e.target.value)}
                placeholder={t('choose.iAmSomeone')}
                rows={5}
                maxLength={1000}
                autoFocus
                className="w-full px-4 py-3 rounded-xl border text-sm leading-relaxed resize-none transition-all focus:outline-none"
                style={{ borderColor: '#C4B5D4', background: 'white', color: '#1C1128' }}
                onFocus={e => { e.target.style.borderColor = '#9B72C0'; e.target.style.boxShadow = '0 0 0 3px rgba(155,114,192,0.12)' }}
                onBlur={e => { e.target.style.borderColor = '#C4B5D4'; e.target.style.boxShadow = 'none' }}
              />

              <div className="flex-1" />

              <button
                onClick={() => onComplete(choiceText.trim())}
                disabled={!identityText.trim()}
                className="w-full py-4 rounded-xl font-medium tracking-wide mt-6 mb-2"
                style={{
                  background: identityText.trim() ? '#3D1F62' : '#E4D5EE',
                  color: identityText.trim() ? 'white' : '#9B8FAA',
                  border: 'none',
                  cursor: identityText.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                {t('choose.completeSession')}
              </button>
              <button
                onClick={() => onComplete(choiceText.trim())}
                className="w-full py-2 text-sm"
                style={{ background: 'none', border: 'none', color: '#9B8FAA', cursor: 'pointer', textDecoration: 'underline' }}
              >
                {t('session.skip')}
              </button>

            </div>
          )}

        </div>
      </div>
    )
  }

  return (
    <>
      {renderScreen()}

      {/* The card the person opened. Kept alive once opened so that Back from
          the screens after it lands on the card's last screen as it was left. */}
      {cardOpened && (
        <Retained active={uiState === 'showing-card'}>
          <InsightCard
            cardType="message_within"
            sessionId={sessionId}
            userId={userId}
            emotion={emotion}
            intensity={5}
            firstName={firstName}
            storyText={storyText}
            patternReflection={patternReflection}
            reframeResponse={reframeResponse}
            seriesContext={seriesContext}
            onComplete={() => setUiState('new-possibility')}
          />
        </Retained>
      )}
    </>
  )
}
